import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getAllSGRWarrantyRequests } from 'lib/api/sgr';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { SGR_STATES, SGR_TYPES } from 'lib/consts';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { CustomButtonProps } from 'lib/designSystem/HeaderWithActions';
import { useConversionRates } from 'lib/hooks/useConvertionRates';
import { Warranty } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import CreateWarrantyDialog from './components/CreateWarrantyDialog';
import WarrantiesTable from './components/WarrantiesTable';
import WarrantyDetailDialog from './components/WarrantyDetailDialog';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const BIG_PAGE_SIZE = 1000000;

const QUOTED_STATES = [
  'QUOTED',
  'REQUESTED',
  'IN_REVIEW',
  'REVIEWED',
  'APPROVED',
  'REJECTED',
];
const ACTIVE_STATES = ['ACTIVE'];
const EXPIRED_STATES = ['INACTIVE'];
const CLAIMED_STATES = ['CLAIMED'];
const CANCELLED_STATES = ['CANCELLED'];

const SGR = () => {
  const dispatch = useDispatch();
  const { fetchAndUpdateRates } = useConversionRates(dispatch);
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState<Warranty | null>(
    null,
  );

  // Filter states
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [requestId, setRequestId] = useState<number | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [businessIdentifier, setBusinessIdentifier] = useState<string | null>(
    null,
  );
  const [principalRut, setPrincipalRut] = useState<string | null>(null);
  const [guaranteeType, setGuaranteeType] = useState<
    'quoted' | 'active' | 'expired' | 'claimed' | 'cancelled' | null
  >('active');
  const [status, setStatus] = useState<{ value: string; label: string }[]>([]);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isStatusChangeFromToggle, setIsStatusChangeFromToggle] =
    useState(false);

  useEffect(() => {
    if (guaranteeType === 'quoted') {
      setStatus(SGR_STATES.filter((s) => QUOTED_STATES.includes(s.value)));
      setIsStatusChangeFromToggle(true);
    } else if (guaranteeType === 'active') {
      setStatus(SGR_STATES.filter((s) => ACTIVE_STATES.includes(s.value)));
      setIsStatusChangeFromToggle(true);
    } else if (guaranteeType === 'expired') {
      setStatus(SGR_STATES.filter((s) => EXPIRED_STATES.includes(s.value)));
      setIsStatusChangeFromToggle(true);
    } else if (guaranteeType === 'claimed') {
      setStatus(SGR_STATES.filter((s) => CLAIMED_STATES.includes(s.value)));
      setIsStatusChangeFromToggle(true);
    } else if (guaranteeType === 'cancelled') {
      setStatus(SGR_STATES.filter((s) => CANCELLED_STATES.includes(s.value)));
      setIsStatusChangeFromToggle(true);
    }
  }, [guaranteeType]);

  const handleGuaranteeTypeChange = (
    newType: 'quoted' | 'active' | 'expired' | 'claimed' | 'cancelled' | null,
  ) => {
    setGuaranteeType(newType);
    setPagination({ ...pagination, page: 1 });
  };

  const handleStatusChange = (
    newStatus: { value: string; label: string }[],
  ) => {
    setIsStatusChangeFromToggle(false);
    setStatus(newStatus);
  };

  useEffect(() => {
    if (status.length > 0 && isStatusChangeFromToggle) {
      void fetchWarranties({
        page: 1,
        pageSize: pagination.pageSize,
      });
    }
  }, [status, isStatusChangeFromToggle]);

  const GuaranteeTypeFilter = () => (
    <Box>
      <ToggleButtonGroup
        value={guaranteeType}
        exclusive
        onChange={(_, newType) => {
          handleGuaranteeTypeChange(newType);
        }}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: '140px',
          },
        }}
      >
        <ToggleButton value="quoted">Cotizaciones</ToggleButton>
        <ToggleButton value="active">Vigentes</ToggleButton>
        <ToggleButton value="expired">Caducadas</ToggleButton>
        <ToggleButton value="claimed">Siniestrados</ToggleButton>
        <ToggleButton value="cancelled">Anulados</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (requestId) query += `warrantyRequestId=${requestId}&`;
    if (type) query += `type=${type}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (principalRut) query += `principalRut=${principalRut}&`;
    if (status.length > 0)
      query += `status=${status.map((s) => s.value).join(',')}&`;
    if (sortBy) query += `sortBy=${sortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchWarranties = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setIsLoading(true);
    const query = generateQuery({ page, pageSize });
    try {
      const response = await getAllSGRWarrantyRequests(dispatch, query);
      setWarranties(response.data);
      setPagination(response.pagination);
      return response.data;
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenWarrantyDetail = (warrantyId: number) => {
    const warranty = warranties.find((warranty) => warranty.id === warrantyId);
    if (!warranty) return;
    setSelectedWarranty(warranty);
    setOpenDetailDialog(true);
  };

  const handleRefreshWarranties = async (params?: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    if (params?.exportComplete) {
      const query = generateQuery({
        page: 1,
        pageSize: BIG_PAGE_SIZE,
      });
      const response = await getAllSGRWarrantyRequests(dispatch, query);
      return response;
    } else {
      const response = await fetchWarranties({
        page: params?.page ?? pagination.page,
        pageSize: params?.pageSize ?? pagination.pageSize,
      });
      if (response && selectedWarranty) {
        const updatedWarranty = response.find(
          (w: Warranty) => w.id === selectedWarranty.id,
        );
        if (updatedWarranty) setSelectedWarranty(updatedWarranty);
        else setSelectedWarranty(null);
      }
    }
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'ID solicitud',
      value: requestId,
      setter: setRequestId,
    },
    {
      label: 'Rut cliente',
      type: 'autocomplete',
      value: businessIdentifier,
      key: 'businessIdentifier',
      setter: setBusinessIdentifier,
    },
    {
      type: 'autocomplete',
      label: 'Rut mandante',
      value: principalRut,
      key: 'businessIdentifier',
      setter: setPrincipalRut,
    },
    {
      type: 'select',
      label: 'Tipo',
      value: type,
      setter: setType,
      options: [{ label: 'Todos', value: '' }, ...SGR_TYPES],
    },
    {
      type: 'multiple',
      label: 'Estado',
      value: status,
      setter: handleStatusChange,
      options: SGR_STATES,
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() =>
            fetchWarranties({
              page: pagination.page,
              pageSize: pagination.pageSize,
            })
          }
          selectedSortBy={sortBy}
          setSelectedSortBy={setSortBy}
          sortOptions={[{ value: 'createdAt', label: 'Creación' }]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      name: 'Nueva Garantía',
      color: 'primary',
      action: () => setOpenCreateDialog?.(true),
    },
  ];

  useEffect(() => {
    void fetchAndUpdateRates();
  }, []);

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <>
      <WarrantiesTable
        warranties={warranties}
        setWarranties={setWarranties}
        handleOpenWarrantyDetail={handleOpenWarrantyDetail}
        pagination={pagination}
        setPagination={setPagination}
        fetchWarranties={handleRefreshWarranties}
        setIsLoading={setIsLoading}
        buttons={buttons}
        guaranteeTypeFilter={<GuaranteeTypeFilter />}
      />

      <CreateWarrantyDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fetchWarranties={() => void handleRefreshWarranties()}
      />

      <WarrantyDetailDialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        warranty={selectedWarranty}
        refreshWarranty={() => void handleRefreshWarranties()}
      />
    </>
  );
};

export default SGR;
